import { Stack } from '@mui/material';
import debounce from 'lodash/debounce';
import { Component } from 'react';
import { Alert } from '../../components/Alert';
import { Dialog } from '../../components/dialogs/Dialog';
import { EmployeeSelector } from '../../components/selectors/EmployeeSelector';
import { SettingsEmployee } from '../../data/settings/SettingsEmployee';
import { withLocale, WithLocaleProps } from '../../providers/LocaleProvider';

interface AssignEmployeeDialogProps extends WithLocaleProps {
  organization?: any;
  user?: any;
  theme?: any;
  employees?: any;
  position?: any;
  error?: string;
  onCancel?: () => void;
  onSave?: (e: any, position: any, employee: any) => void;
  onLoadEmployees?: (
    updateCache?: boolean,
    searchText?: string,
    pageSize?: number,
    status?: number | null,
    orderBy?: string,
    includeInactive?: boolean
  ) => void;
}

class AssignEmployeeDialogBase extends Component<AssignEmployeeDialogProps, any> {
  debouncedLoadEmployees: ReturnType<typeof debounce>;

  constructor(props: AssignEmployeeDialogProps) {
    super(props);

    this.state = {
      employee: null,
      warnHasJob: false,
    };

    this.debouncedLoadEmployees = debounce((searchText: string) => {
      this.props.onLoadEmployees?.(false, searchText);
    }, 500);
  }

  componentDidMount() {
    const { onLoadEmployees } = this.props;
    if (onLoadEmployees) onLoadEmployees();
  }

  onEmployeeChange(e: any, employee: any) {
    // run only when select data
    this.setState({ employee, warnHasJob: employee && employee.hasJob });
  }

  onEmployeeInputChange(value: string) {
    if (!value?.trim()) return;
    this.setState({ employee: null, warnHasJob: false });
    this.debouncedLoadEmployees(value.trim());
  }

  onSave(e: any) {
    const { onSave, position } = this.props;
    const { employee } = this.state;
    if (onSave) onSave(e, position, employee);
  }

  render() {
    const { theme, employees, onCancel, error, translate } = this.props;
    const { employee, warnHasJob } = this.state;

    return (
      <Dialog
        open={true}
        onClose={onCancel}
        theme={theme}
        fullWidth={true}
        title={translate('assignment.assignEmployee')}
        maxWidth='sm'
        actions={[
          {
            label: translate('common.select'),
            primary: true,
            disabled: employee === null,
            onClick: this.onSave.bind(this),
          },
          {
            label: translate('common.cancel'),
            onClick: onCancel,
          },
        ]}
      >
        <Stack direction='column' spacing={4}>
          {error && (
            <div>
              <Alert severity='error'>{error}</Alert>
            </div>
          )}

          {warnHasJob && (
            <div>
              <Alert severity='info'>
                {translate('assignment.willBeRemovedFromPosition', { name: employee.fullName })}
              </Alert>
            </div>
          )}

          <EmployeeSelector<SettingsEmployee>
            callApi={false}
            filterOptionsProps={(options) => options}
            label={null}
            theme={theme}
            multiple={false}
            placeholder={translate('assignment.searchEmployee')}
            value={employee}
            initialItems={
              employees ? employees.employees.filter((e: SettingsEmployee) => e.isFullAccess && !e.isJobManagedByDirectory) : null
            }
            getItemState={(e) => (e.status === SettingsEmployee.status.inactive ? 'greyed' : null)}
            getItemSecondaryAction={(e: SettingsEmployee) =>
              e.status === SettingsEmployee.status.inactive ? e.status.label : null
            }
            showStatus={false}
            onChange={this.onEmployeeChange.bind(this)}
            onInputChange={this.onEmployeeInputChange.bind(this)}
          />
        </Stack>
      </Dialog>
    );
  }
}

export const AssignEmployeeDialog = withLocale(AssignEmployeeDialogBase);
